import CamposParametrosFiscais, { CampoParametro } from "pages/fiscal/parametrosFiscais/CamposParametrosFiscais";
import BaseCadastro from "components/BaseCadastro";
import { Label } from "components/input/Label";
import Pessoa from "model/administracao/cadastro/Pessoa";
import Produto from "model/administracao/cadastro/produto/Produto";
import ProdutoCliente from "model/fiscal/ProdutoCliente";
import TransacaoNotaFiscal from "model/fiscal/TransacaoNotaFiscal";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { TabPanel, TabView } from "primereact/tabview";
import { useEffect, useState } from "react";
import ProdutoClienteService from "service/fiscal/ProdutoClienteService";
import TransacaoNotaFiscalService from "service/fiscal/TransacaoNotaFiscalService";
import { produtoItemTemplate } from "utils/autocompleteUtils";

const produtoClienteService = new ProdutoClienteService();
const transacaoNotaFiscalService = new TransacaoNotaFiscalService();

const camposGerais: CampoParametro<ProdutoCliente>[] = [
    {
        "chave": "idUnidadeOrganizacional",
        "label": "Unidade organizacional",
        "tipo": "referencia",
        "referencia": "unidadeOrganizacional"
    },
    {
        "chave": "idReducaoImposto",
        "label": "Redução de impostos",
        "tipo": "referencia",
        "referencia": "reducaoImposto"
    },
    {
        "chave": "idDispositivoFiscal",
        "label": "Dispositivo fiscal",
        "tipo": "referencia",
        "referencia": "dispositivoFiscal"
    }
];

const camposIcms: CampoParametro<ProdutoCliente>[] = [
    {
        "chave": "idSituacaoTributaria",
        "label": "Situação tributária",
        "tipo": "referencia",
        "referencia": "situacaoTributaria"
    },
    {
        "chave": "idIcmsEspecial",
        "label": "ICMS especial",
        "tipo": "referencia",
        "referencia": "icmsEspecial"
    },
    {
        "chave": "idSubstituicaoTributaria",
        "label": "Substituição tributária",
        "tipo": "referencia",
        "referencia": "substituicaoTributaria"
    }
];

const camposIpi: CampoParametro<ProdutoCliente>[] = [
    {
        "chave": "aliquotaIpi",
        "label": "Percentual da alíquota de IPI (%)",
        "tipo": "percentual"
    }
];

const camposPis: CampoParametro<ProdutoCliente>[] = [
    {
        "chave": "aliquotaPis",
        "label": "Percentual da alíquota de PIS (%)",
        "tipo": "percentual"
    }
];

const camposCofins: CampoParametro<ProdutoCliente>[] = [
    {
        "chave": "aliquotaCofins",
        "label": "Percentual da alíquota de COFINS (%)",
        "tipo": "percentual"
    }
];

const camposIbsCbs: CampoParametro<ProdutoCliente>[] = [
    {
        "chave": "idClassificacaoTributariaIbsCbs",
        "label": "Classificação tributária IBS/CBS",
        "tipo": "referencia",
        "referencia": "classificacaoTributariaIbsCbs"
    }
];

export default function ProdutoClienteCadastro() {
    const [produtoCliente, setProdutoCliente] = useState(new ProdutoCliente());
    const [produtos, setProdutos] = useState<Produto[]>([]);
    const [clientes, setClientes] = useState<Pessoa[]>([]);
    const [transacoes, setTransacoes] = useState<TransacaoNotaFiscal[]>([]);
    const [erro, setErro] = useState("");
    const [abaAtiva, setAbaAtiva] = useState(0);

    useEffect(() => {
        produtoClienteService.produtos().then(setProdutos)
            .catch(error => console.error("Erro ao buscar produtos:", error));
        produtoClienteService.clientes().then(setClientes)
            .catch(error => console.error("Erro ao buscar clientes:", error));
        transacaoNotaFiscalService.getAll().then(setTransacoes)
            .catch(error => console.error("Erro ao buscar transações fiscais:", error));
    }, []);

    function alterar(changes: Partial<ProdutoCliente>) {
        setProdutoCliente({ ...produtoCliente, ...changes });
        setErro("");
    }

    function validar() {
        if (!produtoCliente.produto?.codigo || !produtoCliente.cliente?.id || !produtoCliente.dataInicioVigencia) {
            setAbaAtiva(0);
            setErro("Informe produto, cliente e início da vigência.");
            return false;
        }
        if (produtoCliente.dataFimVigencia && produtoCliente.dataFimVigencia < produtoCliente.dataInicioVigencia) {
            setAbaAtiva(0);
            setErro("O fim da vigência não pode ser anterior ao início.");
            return false;
        }
        if (produtoCliente.cstIcms && produtoCliente.csosn) {
            setAbaAtiva(1);
            setErro("Informe CST ICMS ou CSOSN, não ambos.");
            return false;
        }
        setErro("");
        return true;
    }

    return (
        <BaseCadastro model={produtoCliente} service={produtoClienteService}
            setModel={(value: ProdutoCliente) => setProdutoCliente(value)}
            getNewModel={() => new ProdutoCliente()} novoCadastro
            validar={validar} pathCadastro="/produtoCliente" urlService="/produtoCliente" tituloPagina="Produto x Cliente">
            <div className="row">
                <label style={{ fontSize: 18, fontWeight: 'bold' }} className="col col-sm-12">Produto x Cliente</label>
            </div>
            <TabView renderActiveOnly={false} activeIndex={abaAtiva} onTabChange={evento => setAbaAtiva(evento.index)}>
                <TabPanel header="Dados Gerais">
                    <div className="row">
                        <div className="col-12 mb-3">
                            <Checkbox inputId="produto-cliente-ativo" checked={produtoCliente.ativo}
                                onChange={(e) => alterar({ ativo: !!e.checked })} />
                            <Label htmlFor="produto-cliente-ativo" className="ms-2">Ativo</Label>
                        </div>
                        <div className="col-12 mb-3">
                            <Label className="d-block mb-2" obrigatorio>Produto</Label>
                            <Dropdown value={produtoCliente.produto?.codigo ?? null} options={produtos}
                                optionLabel="nome" optionValue="codigo" itemTemplate={produtoItemTemplate} filter
                                onChange={(e) => alterar({ produto: produtos.find(item => item.codigo === e.value) ?? null })}
                                style={{ width: "100%" }} />
                        </div>
                        <div className="col-12 mb-3">
                            <Label className="d-block mb-2" obrigatorio>Cliente</Label>
                            <Dropdown value={produtoCliente.cliente?.id ?? null} options={clientes}
                                optionLabel="nome" optionValue="id" filter
                                onChange={(e) => alterar({ cliente: clientes.find(item => item.id === e.value) ?? null })}
                                style={{ width: "100%" }} />
                        </div>
                        <div className="col-12 mb-3">
                            <Label className="d-block mb-2">UF destino</Label>
                            <InputText value={produtoCliente.ufDestino ?? ""} maxLength={2}
                                onChange={(e) => alterar({ ufDestino: e.target.value.toUpperCase() || null })}
                                style={{ width: "100%" }} />
                        </div>
                        <div className="col-12 mb-3">
                            <Label className="d-block mb-2">Transação de nota fiscal</Label>
                            <Dropdown value={produtoCliente.transacaoNotaFiscal?.id ?? null} options={transacoes}
                                optionLabel="descricao" optionValue="id" filter showClear
                                onChange={(e) => alterar({ transacaoNotaFiscal: transacoes.find(item => item.id === e.value) ?? null })}
                                style={{ width: "100%" }} />
                        </div>
                        <div className="col-12 mb-3">
                            <Label className="d-block mb-2" obrigatorio>Início da vigência</Label>
                            <InputText type="date" value={produtoCliente.dataInicioVigencia}
                                onChange={(e) => alterar({ dataInicioVigencia: e.target.value })} style={{ width: "100%" }} />
                        </div>
                        <div className="col-12 mb-3">
                            <Label className="d-block mb-2">Fim da vigência</Label>
                            <InputText type="date" value={produtoCliente.dataFimVigencia ?? ""}
                                onChange={(e) => alterar({ dataFimVigencia: e.target.value || null })} style={{ width: "100%" }} />
                        </div>
                        <div className="col-12 mb-3">
                            <Label className="d-block mb-2">Código do produto no cliente</Label>
                            <InputText value={produtoCliente.codigoProdutoCliente ?? ""} maxLength={30}
                                onChange={(e) => alterar({ codigoProdutoCliente: e.target.value || null })} style={{ width: "100%" }} />
                        </div>
                        <div className="col-12 mb-3">
                            <Label className="d-block mb-2">Descrição na nota fiscal</Label>
                            <InputText value={produtoCliente.descricaoProdutoNota ?? ""} maxLength={120}
                                onChange={(e) => alterar({ descricaoProdutoNota: e.target.value || null })} style={{ width: "100%" }} />
                        </div>
                        <div className="col-12 mb-3">
                            <Label className="d-block mb-2">Unidade comercial</Label>
                            <InputText value={produtoCliente.unidadeComercial ?? ""} maxLength={6}
                                onChange={(e) => alterar({ unidadeComercial: e.target.value || null })} style={{ width: "100%" }} />
                        </div>
                        <div className="col-12 mb-3">
                            <Label className="d-block mb-2">EAN</Label>
                            <InputText value={produtoCliente.ean ?? ""} maxLength={14}
                                onChange={(e) => alterar({ ean: e.target.value || null })} style={{ width: "100%" }} />
                        </div>
                        <div className="col-12 mb-3">
                            <Label className="d-block mb-2">CFOP</Label>
                            <InputText value={produtoCliente.cfop ?? ""} maxLength={4}
                                onChange={(e) => alterar({ cfop: e.target.value || null })} style={{ width: "100%" }} />
                        </div>
                        <div className="col-12 mb-3">
                            <Label className="d-block mb-2">Observação</Label>
                            <InputTextarea value={produtoCliente.observacao ?? ""} rows={3}
                                onChange={(e) => alterar({ observacao: e.target.value || null })} style={{ width: "100%" }} />
                        </div>
                        <CamposParametrosFiscais dados={produtoCliente} campos={camposGerais}
                            onChange={alterar} vertical larguraCampo="100%" />
                    </div>
                </TabPanel>
                <TabPanel header="ICMS">
                    <div className="row">
                        <div className="col-12 mb-3">
                            <Label className="d-block mb-2">CST ICMS</Label>
                            <InputText value={produtoCliente.cstIcms ?? ""} maxLength={2}
                                onChange={(e) => alterar({ cstIcms: e.target.value || null })} style={{ width: "100%" }} />
                        </div>
                        <div className="col-12 mb-3">
                            <Label className="d-block mb-2">CSOSN</Label>
                            <InputText value={produtoCliente.csosn ?? ""} maxLength={3}
                                onChange={(e) => alterar({ csosn: e.target.value || null })} style={{ width: "100%" }} />
                        </div>
                        <div className="col-12 mb-3">
                            <Label className="d-block mb-2">Benefício fiscal</Label>
                            <InputText value={produtoCliente.beneficioFiscal ?? ""} maxLength={10}
                                onChange={(e) => alterar({ beneficioFiscal: e.target.value || null })} style={{ width: "100%" }} />
                        </div>
                        <div className="col-12 mb-3">
                            <Label className="d-block mb-2">Alíquota ICMS (%)</Label>
                            <InputNumber locale="pt-BR" value={produtoCliente.aliquotaIcms} min={0} max={100} maxFractionDigits={4}
                                onValueChange={(e) => alterar({ aliquotaIcms: e.value ?? null })} style={{ width: "100%" }} />
                        </div>
                        <div className="col-12 mb-3">
                            <Label className="d-block mb-2">Alíquota ICMS UF destino (%)</Label>
                            <InputNumber locale="pt-BR" value={produtoCliente.aliquotaIcmsUfDestino} min={0} max={100} maxFractionDigits={4}
                                onValueChange={(e) => alterar({ aliquotaIcmsUfDestino: e.value ?? null })} style={{ width: "100%" }} />
                        </div>
                        <div className="col-12 mb-3">
                            <Label className="d-block mb-2">FCP UF destino (%)</Label>
                            <InputNumber locale="pt-BR" value={produtoCliente.aliquotaFcpUfDestino} min={0} max={100} maxFractionDigits={4}
                                onValueChange={(e) => alterar({ aliquotaFcpUfDestino: e.value ?? null })} style={{ width: "100%" }} />
                        </div>
                        <CamposParametrosFiscais dados={produtoCliente} campos={camposIcms}
                            onChange={alterar} vertical larguraCampo="100%" />
                    </div>
                </TabPanel>
                <TabPanel header="IPI">
                    <div className="row">
                        <div className="col-12 mb-3">
                            <Label className="d-block mb-2">CST IPI</Label>
                            <InputText value={produtoCliente.cstIpi ?? ""} maxLength={2}
                                onChange={(e) => alterar({ cstIpi: e.target.value || null })} style={{ width: "100%" }} />
                        </div>
                        <div className="col-12 mb-3">
                            <Label className="d-block mb-2">Código de enquadramento IPI</Label>
                            <InputText value={produtoCliente.codigoEnquadramentoIpi ?? ""} maxLength={3}
                                onChange={(e) => alterar({ codigoEnquadramentoIpi: e.target.value || null })} style={{ width: "100%" }} />
                        </div>
                        <CamposParametrosFiscais dados={produtoCliente} campos={camposIpi}
                            onChange={alterar} vertical larguraCampo="100%" />
                    </div>
                </TabPanel>
                <TabPanel header="PIS">
                    <div className="row">
                        <div className="col-12 mb-3">
                            <Label className="d-block mb-2">CST PIS</Label>
                            <InputText value={produtoCliente.cstPis ?? ""} maxLength={2}
                                onChange={(e) => alterar({ cstPis: e.target.value || null })} style={{ width: "100%" }} />
                        </div>
                        <CamposParametrosFiscais dados={produtoCliente} campos={camposPis}
                            onChange={alterar} vertical larguraCampo="100%" />
                    </div>
                </TabPanel>
                <TabPanel header="COFINS">
                    <div className="row">
                        <div className="col-12 mb-3">
                            <Label className="d-block mb-2">CST COFINS</Label>
                            <InputText value={produtoCliente.cstCofins ?? ""} maxLength={2}
                                onChange={(e) => alterar({ cstCofins: e.target.value || null })} style={{ width: "100%" }} />
                        </div>
                        <CamposParametrosFiscais dados={produtoCliente} campos={camposCofins}
                            onChange={alterar} vertical larguraCampo="100%" />
                    </div>
                </TabPanel>
                <TabPanel header="IBS/CBS">
                    <div className="row">
                        <CamposParametrosFiscais dados={produtoCliente} campos={camposIbsCbs}
                            onChange={alterar} vertical larguraCampo="100%" />
                    </div>
                </TabPanel>
            </TabView>
            {erro && <small className="p-error">{erro}</small>}
        </BaseCadastro>
    );
}
